import { ActionIcon, Center, Container, Group, Loader, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconBrain, IconFileText, IconRoute, IconSettings } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { isUuid } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { PersonaSubscription } from './-components/graphql';
import type { PersonaDetail } from './-components/graphql';
import { PersonaInstructionsPanel } from './-components/persona-instructions-panel';
import { PersonaKnowledgePanel } from './-components/persona-knowledge-panel';
import { PersonaOutputsPanel } from './-components/persona-outputs-panel';
import { PersonaSettingsPanel } from './-components/persona-settings-panel';

export const Route = createFileRoute('/_authed/personas/$personaId')({
  component: PersonaDetailPage,
});

function PersonaDetailPage() {
  const { personaId } = Route.useParams();
  const hasValidId = isUuid(personaId);

  const [{ data, error }] = useSubscription({
    query: PersonaSubscription,
    variables: { personaId },
    pause: !hasValidId,
  });

  if (error) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={getErrorMessage(error, 'Failed to load persona')} />
      </Container>
    );
  }

  if (hasValidId && data === undefined) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!data?.personasByPk || data.personasByPk.deletedAt !== null) {
    return (
      <Center h="50vh">
        <Text c="dimmed">Persona not found</Text>
      </Center>
    );
  }

  return <PersonaWorkspace key={data.personasByPk.id} persona={data.personasByPk} />;
}

function PersonaWorkspace({ persona }: { persona: PersonaDetail }) {
  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group gap="md">
          <ActionIcon variant="subtle" component={Link} to="/personas">
            <IconArrowLeft size={18} />
          </ActionIcon>
          <div>
            <Title order={1}>{persona.name}</Title>
            <Text c="dimmed" mt="xs">
              {persona.description || 'No description'}
            </Text>
          </div>
        </Group>

        <Tabs defaultValue="settings">
          <Tabs.List>
            <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
              Settings
            </Tabs.Tab>
            <Tabs.Tab value="instructions" leftSection={<IconBrain size={16} />}>
              Instructions
            </Tabs.Tab>
            <Tabs.Tab value="outputs" leftSection={<IconRoute size={16} />}>
              Outputs
            </Tabs.Tab>
            <Tabs.Tab value="knowledge" leftSection={<IconFileText size={16} />}>
              Knowledge Base
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings" pt="lg">
            <PersonaSettingsPanel persona={persona} />
          </Tabs.Panel>

          <Tabs.Panel value="instructions" pt="lg">
            <PersonaInstructionsPanel persona={persona} />
          </Tabs.Panel>

          <Tabs.Panel value="outputs" pt="lg">
            <PersonaOutputsPanel persona={persona} />
          </Tabs.Panel>

          <Tabs.Panel value="knowledge" pt="lg">
            <PersonaKnowledgePanel personaId={persona.id} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
